/**
 * L'onglet Rapports de la fiche d'un élève : ses rapports de live (`getStudentLiveReports`, la liste
 * du parent, `liveReportListAdapter`) et son diagnostic pédagogique (`getStudentLiveReportsWithDiagnostic`).
 *
 * Deux choses propres au tuteur :
 * - **les rapports d'un seul élève** : la liste est rangée dans une tranche partagée
 *   (`liveReports.liveReports`, que le parent lit aussi et que chaque élève ouvert remplace). Au large,
 *   on passe d'un élève à l'autre sans quitter l'écran : tant que la page 1 du nouvel élève n'est pas
 *   arrivée, les lignes de l'élève d'avant ne doivent pas s'afficher sous son nom. Chaque rapport porte
 *   son élève (`LiveReportResource.student`) ;
 * - **le diagnostic** de `DiagnosticModal.js` en lecture (`editable={false}`) : un par rapport qui en a
 *   un, du plus récent au plus ancien (l'ordre servi), daté de la création du rapport
 *   (`formatDateNumeric(created_at)`). Le texte est celui que la modale lisait : le diagnostic rangé par
 *   le réducteur (`liveReports.diagnostics`, par `live_report_id`, à jour d'une sauvegarde de fin de
 *   séance), sinon celui de la réponse.
 */
import moment from "moment"

import { type ApiLiveReportListItem } from "../../parent/LiveReports/liveReportListAdapter"
import { type UITutorStudentDiagnostic } from "../../../ui/types/tutorEleves"

/** Une ligne de la liste, avec l'élève que le tuteur ne lit que pour la trier. */
export type ApiTutorStudentReportRow = ApiLiveReportListItem & { student?: { id?: string | number | null } | null }

/** Un rapport qui porte un diagnostic (`live-reports-with-diagnostic`). */
export type ApiReportWithDiagnostic = {
    id?: string | number | null
    created_at?: string | null
    student_diagnostic?: { live_report_id?: string | number | null; diagnostic?: string | null } | null
}

/** Un diagnostic gardé par le réducteur `liveReports` (`mergeDiagnostics`). */
export type StoredDiagnostic = { live_report_id?: string | number | null; diagnostic?: string | null }

/** Les rapports de cet élève, dans l'ordre servi. Une ligne sans élève reste : rien ne dit qu'elle est d'un autre. */
export function reportsOfStudent<Row extends ApiTutorStudentReportRow>(
    rows: ReadonlyArray<Row | null | undefined> | null | undefined,
    studentId: string | number | null | undefined
): Row[] {
    if (studentId == null) return []
    return (rows ?? []).filter(
        (row): row is Row => row != null && (row.student?.id == null || String(row.student.id) === String(studentId))
    )
}

export function toUITutorStudentDiagnostics(
    reports: ReadonlyArray<ApiReportWithDiagnostic | null | undefined> | null | undefined,
    stored: ReadonlyArray<StoredDiagnostic | null | undefined> | null | undefined
): UITutorStudentDiagnostic[] {
    return (reports ?? [])
        .filter((report): report is ApiReportWithDiagnostic => report?.id != null)
        .map((report) => {
            const kept = (stored ?? []).find(
                (diagnostic) =>
                    diagnostic?.live_report_id != null && String(diagnostic.live_report_id) === String(report.id)
            )
            return {
                id: report.id!,
                // `formatDateNumeric` : « - » sans date, comme l'ancienne modale.
                dateLabel: report.created_at ? moment(report.created_at).format("DD/MM/YYYY") : "-",
                text: kept?.diagnostic ?? report.student_diagnostic?.diagnostic ?? null,
            }
        })
}

/**
 * La lecture du diagnostic a répondu « aucun » : `live-reports-with-diagnostic` répond 404 quand
 * l'élève n'a aucun rapport avec diagnostic (« Aucun rapport avec diagnostic trouvé pour cet élève. »).
 * Ce n'est pas une panne : la fiche n'a pas de bloc.
 */
export function isNoDiagnosticAnswer(error: unknown): boolean {
    return (error as { status?: unknown } | null | undefined)?.status === 404
}
